import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { GeneratePage } from '@ui/generatePage';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { DeviceType } from '@repo/types';
import { useCmsPageLayoutQuery } from '@data/cms/useCmsPageLayoutQuery';
import { NotFoundPage } from '../NotFoundPage';
import { Loader } from '@ui/common/components/Loader/Loader';

export const CmsPage = () => {
  const location = useLocation();
  const deviceType = useDeviceType();

  const { data: pageData, isLoading, error } = useCmsPageLayoutQuery(location.pathname);

  const { rootChildren, rows, columns, components } = useMemo(() => {
    if (!pageData) {
      return {};
    }

    switch (deviceType) {
      case DeviceType.mobile:
        return {
          rootChildren:
            pageData.mobileLayout?.rootChildren ??
            pageData.tabletLayout?.rootChildren ??
            pageData.desktopLayout?.rootChildren,
          rows: pageData.mobileLayout?.rows ?? pageData.tabletLayout?.rows ?? pageData.desktopLayout?.rows,
          columns: pageData.mobileLayout?.columns ?? pageData.tabletLayout?.columns ?? pageData.desktopLayout?.columns,
          components:
            pageData.mobileLayout?.components ??
            pageData.tabletLayout?.components ??
            pageData.desktopLayout?.components,
        };
      case DeviceType.tablet:
        return {
          rootChildren:
            pageData.tabletLayout?.rootChildren ??
            pageData.mobileLayout?.rootChildren ??
            pageData.desktopLayout?.rootChildren,
          rows: pageData.tabletLayout?.rows ?? pageData.mobileLayout?.rows ?? pageData.desktopLayout?.rows,
          columns: pageData.tabletLayout?.columns ?? pageData.mobileLayout?.columns ?? pageData.desktopLayout?.columns,
          components:
            pageData.tabletLayout?.components ??
            pageData.mobileLayout?.components ??
            pageData.desktopLayout?.components,
        };
      case DeviceType.desktop:
        return {
          rootChildren: pageData.desktopLayout?.rootChildren,
          rows: pageData.desktopLayout?.rows,
          columns: pageData.desktopLayout?.columns,
          components: pageData.desktopLayout?.components,
        };
      default:
        return {};
    }
  }, [deviceType, pageData]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (error) {
    return <NotFoundPage />;
  }

  const metaTitle =
    pageData?.meta?.title ?? `${import.meta.env.VITE_SITE_NAME} Football News, Fixtures, Scores & Results`;
  const metaDescription =
    pageData?.meta?.description ??
    `For all the latest ${import.meta.env.VITE_ENTITY_NAME} news, visit the official website of the ${
      import.meta.env.VITE_SITE_NAME
    }.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <GeneratePage rootChildren={rootChildren} rows={rows} columns={columns} components={components} />
    </>
  );
};
